import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";

import tokens from "src/shared/theme/tokens";

/**
 * Full-height canvas painted with the brand page background. The split-hero
 * breakpoint is `md` (990px): below it the screen keeps the original stacked
 * mobile layout (banner, then panel); at/above it the banner and the
 * sign-in panel sit side by side.
 */
export const LandingRoot = styled(Box)(
  ({ theme }) => `
     display: flex;
     flex-direction: column;
     flex: 1;
     width: 100%;
     background: ${tokens.colors.pageBg};

     @media (max-width: ${theme.breakpoints.values.sm}px) {
       min-height: calc(var(--vh, 1vh) * 100);
     }
`
);

export const HeroLayout = styled(Box)(
  ({ theme }) => `
     display: flex;
     flex: 1;
     flex-direction: column;
     justify-content: center;
     position: relative;
     width: 100%;
     max-width: ${theme.breakpoints.values.sm}px;
     margin: 0 auto;
     padding: 0 ${theme.spacing(4)};

     @media (min-width: ${theme.breakpoints.values.md}px) {
       flex-direction: row;
       align-items: stretch;
       justify-content: center;
       gap: ${theme.spacing(12)};
       max-width: ${theme.breakpoints.values.lg}px;
       min-height: calc(var(--vh, 1vh) * 100);
       padding: ${theme.spacing(10)} ${theme.spacing(8)};
     }
`
);

/**
 * Left panel at >= 990px; the banner block at the top of the stacked layout
 * below it.
 */
export const HeroBanner = styled(Box)(
  ({ theme }) => `
     display: flex;
     flex-direction: column;
     width: 100%;

     @media (min-width: ${theme.breakpoints.values.md}px) {
       flex: 1 1 55%;
       align-items: center;
       justify-content: center;
       min-width: 0;
     }
`
);

/**
 * Right panel at >= 990px (brand lockup + subtitle + auth buttons, centered
 * vertically); the buttons block below the banner in the stacked layout.
 */
export const HeroPanel = styled(Box)(
  ({ theme }) => `
     display: flex;
     flex-direction: column;
     width: 100%;

     @media (min-width: ${theme.breakpoints.values.md}px) {
       flex: 1 1 45%;
       justify-content: center;
       max-width: 420px;
     }
`
);

/**
 * Brand lockup + property subtitle. Only shown in the desktop right panel —
 * below 990px the legacy header carries the logo and property name instead.
 */
export const HeroBrand = styled(Box)(
  ({ theme }) => `
     display: none;

     @media (min-width: ${theme.breakpoints.values.md}px) {
       display: flex;
       flex-direction: column;
       align-items: flex-start;
       margin-bottom: ${theme.spacing(8)};
     }

     img {
       display: block;
       height: 40px;
       width: auto;
     }
`
);

export const LandingBannerContainer = styled(Box)(
  ({ theme }) => `
     display: flex;
     width: 100%;
     overflow: hidden;
     position: relative;
     justify-content: center;
     margin-bottom: ${theme.spacing(4)};
     border-radius: ${tokens.radius.md}px;

     @media (min-width: ${theme.breakpoints.values.md}px) {
       flex: 1;
       width: 100%;
       height: 100%;
       margin-bottom: 0;
     }
`
);

/**
 * Real property banner (CloudFront photo). Fills the left panel edge-to-edge
 * on desktop; keeps the contained, height-capped presentation on mobile.
 */
export const LandingBanner = styled(Box)(
  ({ theme }) => `
     display: flex;
     width: 100%;
     overflow: hidden;
     position: relative;
     justify-content: center;
     border-radius: ${tokens.radius.md}px;

     img {
      object-fit: contain;
      object-position: center;
      border-radius: ${tokens.radius.md}px;
     }

     @media (max-width: ${theme.breakpoints.values.sm}px) {
      img {
        width: 100%;
        max-height: calc((var(--vh, 1vh) * 100) - 200px);
      }
     }

     @media (min-width: ${theme.breakpoints.values.sm}px) {
       img {
        width: 100%;
        max-width: 380px;
       }
     }

     @media (min-width: ${theme.breakpoints.values.md}px) {
       width: 100%;
       height: 100%;
       border-radius: ${tokens.radius.lg}px;
       box-shadow: ${tokens.shadows.card};

       img {
        width: 100%;
        height: 100%;
        max-width: none;
        object-fit: cover;
        border-radius: ${tokens.radius.lg}px;
       }
     }
`
);

/**
 * Default fallback illustration (shown when the property banner 404s). Stays
 * contained and centered — never cropped — in both layouts.
 */
export const DefaultBanner = styled(Box)(
  ({ theme }) => `
     display: flex;
     width: 100%;
     overflow: hidden;
     position: relative;
     justify-content: center;
     align-items: center;
     border-radius: ${tokens.radius.md}px;
     flex-direction: column;

     img {
      display: block;
      width: auto;
      max-width: 90%;
      height: auto;
      max-height: 180px;
     }

     @media (min-width: ${theme.breakpoints.values.md}px) {
       width: 100%;
       height: 100%;

       img {
        max-height: 320px;
       }
     }
`
);
